import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
    ImageItem,
    InstagramAccountData,
    InstagramResponse,
    MODULES_OPTIONS,
} from '@app/_models/images-manager/images-management.model';
import { Select, Store } from '@ngxs/store';
import {
    ClearInstagramPhotos,
    GetInstagramPhotos,
    LogoutInstagram,
    SaveInstagramToken,
    UploadPhoto,
} from '../../store/images.actions';
import { ImagesState } from '../../store/images.state';
import { Observable } from 'rxjs';
import { IMAGE_MANAGER_DIALOG_DATA } from '../../img-modal.component';
import { McToastService } from '@app/_services';
import { TranslateService } from '@ngx-translate/core';
import { CarouselModalComponent } from '../carousel-modal/carousel-modal.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'instagram-upload',
    templateUrl: 'instagram-upload.component.html',
    styleUrls: ['instagram-upload.component.scss'],
})
export class InstagramUploadComponent implements OnInit, OnDestroy {
    @Input() data: IMAGE_MANAGER_DIALOG_DATA;
    loading: boolean = false;
    authWindow: Window | null = null;

    @Select(ImagesState.getInstagramPhotos)
    instagramPhotos$: Observable<any>;

    @Select(ImagesState.getInstagramNext)
    instagramNext$: Observable<string>;

    instagramNext: string;

    // instagramPhotos: InstagramPhoto[];
    instagramPhotos: string[] = [];

    selectedFiles: Set<string> = new Set();

    @Select(ImagesState.getInstagramConfig)
    instagramResponse$: Observable<InstagramResponse>;

    instagramResponse: InstagramResponse;

    @Select(ImagesState.getAccountData)
    instagramAccountData$: Observable<any>;

    instagramAccountData: InstagramAccountData;

    @Output() closeDialog: EventEmitter<boolean> = new EventEmitter();

    firtsLoad: boolean = true;

    constructor(
        private store: Store,
        private sanitizer: DomSanitizer,
        private toast: McToastService,
        private translate: TranslateService,
        private dialog: MatDialog
    ) {
        this.loading = true;
        this.store.dispatch(new GetInstagramPhotos()).subscribe({
            complete: () => (this.loading = false),
        });

        this.instagramPhotos$.subscribe((data) => {
            if (data?.length) {
                this.instagramPhotos = this.instagramPhotos.concat(data);
            }
        });

        this.instagramNext$.subscribe((data) => {
            this.instagramNext = data;
        });

        this.instagramAccountData$.subscribe((data) => {
            this.instagramAccountData = data;
        });

        this.instagramResponse$.subscribe((instagramResponse) => {
            this.instagramResponse = instagramResponse;
        });
    }

    ngOnInit(): void {
        window.addEventListener('message', this.handleMessage, false);
    }

    ngOnDestroy(): void {
        window.removeEventListener('message', this.handleMessage, false);
        this.store.dispatch(new ClearInstagramPhotos());
    }

    login(): void {
        const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            this.instagramResponse.url ?? ''
        ) as unknown as string;
        const fixedAuthUrl = decodeURIComponent(this.instagramResponse.url);

        // Calcular el tamaño del popup como un porcentaje del tamaño de la pantalla
        const popupWidth = window.screen.width * 0.3; // 30% del ancho de la pantalla
        const popupHeight = window.screen.height * 0.7; // 70% de la altura de la pantalla

        // // Obtener el tamaño de la ventana del navegador
        // const left = window.screen.width / 2 - popupWidth / 2;
        // const top = window.screen.height / 2 - popupHeight / 2;

        // Calcular la posición central del popup con respecto a la pantalla
        const left = window.screenX + window.innerWidth / 2 - popupWidth / 2;
        const top = window.screenY + window.innerHeight / 2 - popupHeight / 2;

        // Configurar las características del popup
        const popupFeatures = `width=${popupWidth},height=${popupHeight},top=${top},left=${left},scrollbars=no,resizable=no`;

        // Abrir el popup centrado
        this.authWindow = window.open(fixedAuthUrl, 'Instagram', popupFeatures);

        if (window.focus && this.authWindow) {
            this.authWindow.focus();
        }
    }

    private handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
            const code = event?.data?.code;
            if (code) {
                if (this.authWindow) {
                    this.authWindow.close();
                }
                this.loading = true;
                this.store.dispatch(new SaveInstagramToken(code)).subscribe({
                    next: () => {
                        console.log('SaveInstagramToken dispatched successfully.');
                    },
                    error: (error) => {
                        console.error('Error saving Instagram token:', error);
                        this.loading = false;
                    },
                    complete: () => {
                        console.log('Termino el save token');
                        const updatedInstagramConfig = this.store.selectSnapshot(ImagesState.getInstagramConfig);
                        if (updatedInstagramConfig?.loggedIn) {
                            this.store.dispatch(new GetInstagramPhotos()).subscribe({
                                complete: () => (this.loading = false),
                                error: (error) => (this.loading = false),
                            });
                        } else {
                            this.loading = false;
                        }
                    },
                });
            }
        }
    };

    getImageUrl(value: string): string {
        return decodeURIComponent(value);
    }

    toggleFileSelection(url: string): void {
        if (this.selectedFiles.has(url)) {
            this.selectedFiles.delete(url);
        } else {
            if (this.selectedFiles.size === 15) {
                this.toast.danger(this.translate.instant('app.producto.toast.error_max_imagen', { limit: 15 }));
                return;
            }
            this.selectedFiles.add(url);
        }
    }

    reset(): void {
        if (this.selectedFiles.size >= 1) {
            this.selectedFiles.clear();
        } else {
            this.closeDialog.emit(true);
        }
    }

    upload(): void {
        this.loading = true;
        const formData: FormData = new FormData();

        let formIndex = 0;
        const selectedFiles = Array.from(this.selectedFiles);
        selectedFiles.forEach((url) => {
            const file = this.getImageUrl(url);
            formData.append(`data[${formIndex}][file]`, file);
            formData.append(`data[${formIndex}][relation_id]`, this.data.relation_id.toString());
            formData.append(`data[${formIndex}][module]`, this.data.module);
            formData.append(`data[${formIndex}][type]`, 'image');
            formData.append(`data[${formIndex}][url]`, 'true');
            formIndex++;
        });

        this.store.dispatch(new UploadPhoto(formData)).subscribe({
            complete: () => (this.loading = false),
            error: () => (this.loading = false),
        });
    }

    onScroll(event: any) {
        if (
            Math.ceil(event.target.offsetHeight) + Math.ceil(event.target.scrollTop) >= event.target.scrollHeight &&
            this.instagramNext
        ) {
            this.loading = true;
            this.store.dispatch(new GetInstagramPhotos(this.instagramNext)).subscribe({
                complete: () => (this.loading = false),
                error: (error) => (this.loading = false),
            });
        }
    }

    logout(): void {
        this.loading = true;
        this.store.dispatch(new LogoutInstagram()).subscribe({
            next: () => {
                this.instagramPhotos = [];
                this.instagramNext = null;
                this.selectedFiles.clear();
            },
            complete: () => (this.loading = false),
            error: (error) => (this.loading = false),
        });
    }

    openCarouselModal(index: number): void {
        const images: ImageItem[] = [];
        this.instagramPhotos.forEach((url: string, index) => {
            const item: ImageItem = {
                id: index,
                filetype: 'image',
                filepath: url,
                filename: url,
                module: MODULES_OPTIONS.ALL,
                relation_id: this.data.relation_id.toString(),
                url: this.getImageUrl(url),
                size: url.length,
                use_module: [],
            };
            images.push(item);
        });
        this.dialog.open(CarouselModalComponent, {
            data: { images, index },
            width: '80%',
            // height: '80%',
            panelClass: 'full-image-modal',
        });
    }
}
